import { FC, useEffect, useRef, useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { Button } from '$uikit';
import { useTranslator } from '$hooks';
import {
  cancelAuthorization,
  ceremonyCancelled,
  submitAuthorization,
  subscribeAuthorization,
  type AuthenticationCeremony,
} from './authorization';
import './PasskeyAuthorization.sass';

export const PasskeyAuthorization: FC = () => {
  const t = useTranslator();
  const closeRef = useRef<(() => void) | null>(null);
  const shownRef = useRef<AuthenticationCeremony | null>(null);
  const closingRef = useRef(false);
  const [request, setRequest] = useState<AuthenticationCeremony | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeAuthorization((next) => {
      if (next) {
        closingRef.current = false;
        shownRef.current = next;
        setRequest(next);
        setBusy(false);
        return;
      }
      if (closingRef.current || !shownRef.current) return;
      closingRef.current = true;
      closeRef.current?.();
    });
    return () => {
      unsubscribe();
      cancelAuthorization();
    };
  }, []);

  const release = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    cancelAuthorization('passkey_cancelled');
  };

  const finishClose = () => {
    closingRef.current = true;
    shownRef.current = null;
    setRequest(null);
    setBusy(false);
  };

  if (!request) return null;

  return (
    <BottomSheet
      key={request.ceremonyId}
      className="PasskeyAuthorization"
      title={t('passkey_confirm_title', { defaultValue: 'Confirm with your passkey' })}
      onClose={finishClose}
      onClosing={release}
      closeRef={closeRef}
    >
      <div className="PasskeyAuthorization__body">
        <p className="PasskeyAuthorization__text">
          {t('passkey_confirm_text', {
            defaultValue:
              'To continue, confirm this action with the passkey on your device.',
          })}
        </p>
        <div className="PasskeyAuthorization__actions">
          <Button
            stretched
            disabled={busy}
            onClick={async () => {
              if (busy) return;
              setBusy(true);
              try {
                let credential: unknown;
                try {
                  credential = await startAuthentication({
                    optionsJSON: request.optionsJSON,
                  });
                } catch (error) {
                  cancelAuthorization(
                    ceremonyCancelled(error) ? 'passkey_cancelled' : 'passkey_auth_error',
                  );
                  return;
                }
                try {
                  await submitAuthorization(credential);
                } catch {
                  return;
                }
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('confirm_code_confirmation_button')}
          </Button>
          <Button
            stretched
            mode="secondary"
            disabled={busy}
            onClick={() => cancelAuthorization('passkey_cancelled')}
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};
